import React, { useEffect, useState, useCallback } from "react";
import usePersistentState from "../../../../hooks/usePersistentState";
import {
  Typography,
  useTheme,
  useMediaQuery,
  Box,
  Button,
  TextField,
  Autocomplete,
  Paper,
  Card,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
  Tooltip,
  Chip
} from "@mui/material";
import { countries } from "country-list-json";
import {
  DataGrid,
  GridToolbar,
  getGridStringOperators,
} from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import DeleteIcon from '@mui/icons-material/Delete';
import SortIcon from '@mui/icons-material/Sort';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import { tokens } from "../../../../theme";
import { API_URL } from "../../../../api";
import { useAppContext } from "../../../../Context/UseContext";

const ReportList = () => {
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [page, setPage] = usePersistentState("admin_reports_page", 0);
  const [pageSize, setPageSize] = usePersistentState("admin_reports_pageSize", 25);
  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = usePersistentState("admin_reports_filterModel", { items: [] });
  const [selectionModel, setSelectionModel] = useState([]);
  const [sortModel, setSortModel] = usePersistentState("admin_reports_sort", [{ field: 'created_at', sort: 'desc' }]);
  const [selectedCountry, setSelectedCountry] = usePersistentState("admin_reports_country", null);
  const [selectedStatus, setSelectedStatus] = usePersistentState("admin_reports_status", "");
  
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery("(max-width:768px)");
  const { showFullPhoneNumberemail } = useAppContext();

  const containsOperator = getGridStringOperators().find(
    (op) => op.value === "contains"
  );

  const getApprovalStatus = (approval, deleted_at) => {
    if (deleted_at) return "Deleted";
    switch (approval) {
      case 10: return "Pending";
      case 15: return "Disable";
      case 20: return "Approved";
      case 30: return "Rejected";
      case 40: return "Incomplete Registration";
      case 50: return "Resolved";
      default: return "N/A";
    }
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (phoneNumber && phoneNumber?.length >= 6) {
      const visibleDigits = 3;
      const maskedSection = phoneNumber
        .substring(visibleDigits, phoneNumber?.length - visibleDigits)
        .replace(/\d/g, "*");
      return phoneNumber.substring(0, visibleDigits) + maskedSection + phoneNumber.substring(phoneNumber?.length - visibleDigits);
    }
    return phoneNumber || "N/A";
  };

  const formatEmailAddress = (email) => {
    if (email && email !== "N/A") {
      const parts = email.split("@");
      return `${parts[0].charAt(0)}***@${parts[1]}`;
    }
    return "N/A";
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const isClientFilter = selectedCountry || selectedStatus !== "";
      // Fetch more data if filtering on client side, otherwise standard page
      const fetchPageSize = isClientFilter ? 5000 : pageSize;
      const fetchPageNo = isClientFilter ? 1 : page + 1;

      const response = await fetch(
        `${API_URL}admin/report/?limit=${fetchPageSize}&pageNo=${fetchPageNo}&status=0,50`
      );
      const data = await response.json();

      let filteredResults = data.results;

      // Client-side filtering
      if (selectedStatus !== "") {
          const targetIsResolved = selectedStatus === "50"; // 50 is Resolved
          filteredResults = filteredResults.filter(item => {
              const currentStatus = item.report_status || 0;
              const isResolved = currentStatus === 50; 
              return isResolved === targetIsResolved;
          });
      }
      if (selectedCountry) {
         filteredResults = filteredResults.filter(item => item.country && item.country.includes(selectedCountry.name));
      }

      const formattedData = filteredResults.map((item) => ({
        id: item.id || item.user_id, // Fallback to user_id if report id is missing
        user_id: item.user_id, 
        name: `${item.first_name} ${item.last_name || ""}`,
        reporter_name: item.reporter_first_name ? `${item.reporter_first_name} ${item.reporter_last_name || ""}` : "N/A",
        reporter_id: item.person_id,
        approval: getApprovalStatus(item.approval, item.deleted_at),
        phone: item.phone || "N/A",
        email: item.email || "N/A",
        country: item.country || "N/A",
        reason: item.reason || "N/A",
        gender: item.gender === 1 ? "Men" : "Women",
        created_at: item.reported_date, // Keep raw for sorting, format in render
        birthday: item.birthday,
        avatar: item.avatar, // If available, though original didn't show it
        report_status: item.report_status // Added report status
      }));

       // Client-side Sorting
       if (sortModel.length > 0) {
        const { field, sort } = sortModel[0];
        formattedData.sort((a, b) => {
          let aValue = a[field];
          let bValue = b[field];

          // Handle dates
          if (field === 'created_at' || field === 'birthday') {
              aValue = new Date(aValue).getTime();
              bValue = new Date(bValue).getTime();
          }
           // Handle strings
          if (typeof aValue === 'string') aValue = aValue.toLowerCase();
          if (typeof bValue === 'string') bValue = bValue.toLowerCase();

          if (aValue < bValue) return sort === 'asc' ? -1 : 1;
          if (aValue > bValue) return sort === 'asc' ? 1 : -1;
          return 0;
        });
      }


      setCustomers(formattedData);
      setRowCount(isClientFilter ? formattedData.length : data.total);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortModel, selectedCountry, selectedStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClearFilters = () => {
    setSelectedCountry(null);
    setSelectedStatus("");
    setSortModel([{ field: 'created_at', sort: 'desc' }]);
    setPage(0);
  };

  const handleBulkResolve = async (status) => {
    const actionName = status === 50 ? "resolve" : "mark as unresolved";
    if (window.confirm(`Are you sure you want to ${actionName} ${selectionModel.length} reports?`)) {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}admin/report/resolve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ user_ids: selectionModel, status }), // Revert to user_ids as we are using user_id as row ID
            });
            
            if (response.ok) {
                setSelectionModel([]);
                fetchData(); // Refresh list
            } else {
                console.error("Failed to update report status");
            }
        } catch (err) {
            console.error("Error updating report status:", err);
        } finally {
            setLoading(false);
        }
    }
  };

  const desktopColumns = [
    {
      field: "name",
      headerName: "User",
      flex: 1.5,
      filterOperators: [containsOperator],
      renderCell: (params) => (
          <Box display="flex" alignItems="center" gap={2}>
            {/* Placeholder Avatar or actual if available */}
             <Avatar sx={{ width: 35, height: 35, bgcolor: colors.greenAccent[500] }}>
                {params.value.charAt(0)}
             </Avatar>
            <Typography variant="body2" fontWeight="bold">
                {params.value}
            </Typography>
          </Box>
      )
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 1.5,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography noWrap variant="body2">{params.value}</Typography>
        </Tooltip>
      ),
    },
    {
        field: "gender",
        headerName: "Gender",
        flex: 0.7,
        filterable: false,
    },
    {
      field: "country",
      headerName: "Location",
      flex: 1,
      filterable: true,
      filterOperators: [containsOperator],
    },

    {
        field: "created_at",
        headerName: "Reported At",
        flex: 1,
        filterable: false,
        valueFormatter: (params) => {
            if (!params.value) return "N/A";
            return new Date(params.value).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        },
    },

    {
      field: "action",
      headerName: "Action",
      flex: 0.7,
      filterable: false,
      renderCell: (params) => (
        <Button
            component={Link}
            to={`/admin/user/report/${params.row.id}`} // Passing Report ID (or User ID as fallback)
            state={{ reportData: params.row }} // Pass data for fallback display
            variant="contained"
            size="small"
            sx={{
                backgroundColor: colors.blueAccent[500],
                '&:hover': {
                    backgroundColor: colors.blueAccent[700],
                }
            }}
        >
          Details
        </Button>
      ),
    },
  ];

    const mobileColumns = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "reason", headerName: "Reason", flex: 1 },
    { 
        field: "action", 
        headerName: "Action", 
        flex: 0.7,
        renderCell: (params) => (
            <Link
              to={`/admin/user/report/${params.row.id}`}
              className="text-primary text-decoration-none"
            >
              Details
            </Link>
          ),
    },
  ];

  return (
    <Card elevation={0} sx={{ backgroundColor: 'transparent', backgroundImage: 'none' }}>
        <Paper 
            elevation={0}
            sx={{ 
                p: 2, 
                mb: 3, 
                backgroundColor: colors.primary[400], 
                borderRadius: '16px',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2
            }}
        >
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                    {/* Status Filter */}


                {/* Location Filter */}
                <Autocomplete
                    options={countries}
                    getOptionLabel={(option) => option.name}
                    value={selectedCountry}
                    onChange={(event, newValue) => {
                        setSelectedCountry(newValue);
                        setPage(0);
                    }}
                    sx={{ width: 220 }}
                    renderInput={(params) => (
                        <TextField 
                            {...params} 
                            label="Location" 
                            variant="outlined" 
                            size="small"
                            sx={{ 
                                '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: theme.palette.background.default }
                            }}
                        />
                    )}
                />
                
                {(selectedCountry) && (
                        <Button onClick={handleClearFilters} color="error" size="small">
                        Clear
                        </Button>
                )}
            </Box>

            <Box display="flex" gap={2} alignItems="center">

                {/* Sort By */}
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Sort by</InputLabel>
                    <Select
                        value={sortModel[0]?.field || "created_at"}
                        label="Sort by"
                        onChange={(e) => {
                            setSortModel([{ field: e.target.value, sort: sortModel[0]?.sort || 'desc' }]);
                            setPage(0);
                        }}
                        startAdornment={<SortIcon sx={{ mr: 1, fontSize: 18, color: colors.grey[400] }} />}
                        sx={{ borderRadius: '12px', bgcolor: theme.palette.background.default }}
                    >
                        <MenuItem value="name">User</MenuItem>
                        <MenuItem value="created_at">Reported At</MenuItem>
                        <MenuItem value="reason">Reason</MenuItem>
                        <MenuItem value="country">Location</MenuItem>
                    </Select>
                </FormControl>

                 <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Order</InputLabel>
                        <Select
                            value={sortModel[0]?.sort || "desc"}
                            label="Order"
                            onChange={(e) => {
                                setSortModel([{ field: sortModel[0]?.field || 'created_at', sort: e.target.value }]);
                                setPage(0);
                            }}
                            sx={{ borderRadius: '12px', bgcolor: theme.palette.background.default }}
                        >
                            <MenuItem value="asc">Ascending</MenuItem>
                            <MenuItem value="desc">Descending</MenuItem>
                        </Select>
                    </FormControl>
            </Box>
        </Paper>

        <Box 
            sx={{ 
                height: 700, 
                width: '100%',
                '& .MuiDataGrid-root': {
                    border: 'none',
                    backgroundColor: colors.primary[400],
                    borderRadius: '16px',
                    padding: '10px'
                },
                '& .MuiDataGrid-cell': {
                    borderBottom: 'none',
                    paddingY: '10px'
                },
                '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'transparent',
                    borderBottom: `1px solid ${colors.grey[800]}`,
                    color: colors.grey[300],
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em'
                },
                '& .MuiDataGrid-row': {
                    borderRadius: '8px',
                    marginTop: '5px',
                    '&:hover': {
                            backgroundColor: colors.blueAccent[800]
                    }
                },
                '& .MuiDataGrid-footerContainer': {
                    borderTop: 'none',
                    justifyContent: 'center'
                },
                '& .MuiCheckbox-root': {
                    color: `${colors.grey[500]} !important`,
                },
            }}
        >
        <DataGrid
            page={page}
            loading={loading}
            pageSize={pageSize}
            rows={customers}
            rowCount={rowCount}
            pagination
            paginationMode={(selectedCountry) ? "client" : "server"}
            sortingMode="client" // Use client sorting for consistency here since we're fetching chunks
            rowsPerPageOptions={[25, 50, 100]}
            // checkboxSelection
            selectionModel={selectionModel}
            onSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
            sortModel={sortModel}
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            components={{ Toolbar: GridToolbar }}
            columns={isMobile ? mobileColumns : desktopColumns}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            filterModel={filterModel}
            onFilterModelChange={(newModel) => setFilterModel(newModel)}
            getRowId={(row) => row.id}
            componentsProps={{
                pagination: {
                sx: {
                    color: theme.palette.text.primary,
                    "& .MuiTablePagination-selectLabel": { color: theme.palette.text.primary },
                    "& .MuiTablePagination-input": { color: theme.palette.text.primary },
                    "& .MuiTablePagination-displayedRows": { color: theme.palette.text.primary },
                    "& .MuiSvgIcon-root": { color: theme.palette.text.primary },
                    "& .MuiButtonBase-root": { color: theme.palette.text.primary },
                    "& .MuiPaginationItem-root": { color: theme.palette.text.primary },
                },
                },
            }}
        />
        </Box>
    </Card>
  );
};

export default ReportList;

